import type { GoalRecord } from '~/models/goal'
import type { PhaseRecord } from '~/models/phase'
import type { WeeklyReviewRecord } from '~/models/weeklyReview'
import { PROJECTS_STORE, SCHEMA_V2_ADDED_INDEXES, STORE } from '../schema'
import { type BackfillPlan, planProjectBackfill } from './backfillProject'
import type { Migration, MigrationContext } from './index'

/**
 * Version 2 — the `projects` store, its three foreign keys, and the backfill
 * that carries existing history into the new model (D-062).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THIS IS THE FIRST MIGRATION THAT WILL RUN AGAINST REAL DATA
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  Version 1 created every store on a fresh install, so it has only ever been a
 *  no-op against an existing database. This one reads records the user cannot
 *  replace and writes them back. Two consequences shape the code below.
 *
 *  **Nothing is awaited.** `run` is synchronous by signature (D-028): every
 *  request is issued here and its continuation runs in `onsuccess`, inside the
 *  same `versionchange` transaction. Awaiting even one non-IndexedDB promise
 *  would let the transaction auto-commit half-applied, with the store created
 *  and the backfill missing.
 *
 *  **Nothing is repaired by hand.** If any step throws — including inside a
 *  success handler — the exception aborts the transaction and IndexedDB rolls
 *  every change back atomically, leaving the stored version at 1. That is the
 *  recovery path, and `boot.ts` reopens at the prior version when it happens.
 */
export const migrationV2: Migration = {
  to: 2,
  describe: 'Add the projects store and carry existing phases, goals and reviews into one project',
  run({ database, transaction }: MigrationContext): void {
    createProjectsStore(database)
    addProjectIdIndexes(transaction)
    backfill(transaction)
  },
}

function createProjectsStore(database: IDBDatabase): void {
  if (database.objectStoreNames.contains(PROJECTS_STORE.name)) return

  const store = database.createObjectStore(PROJECTS_STORE.name, {
    keyPath: PROJECTS_STORE.keyPath,
  })
  for (const index of PROJECTS_STORE.indexes) {
    store.createIndex(index.name, index.keyPath as string)
  }
}

function addProjectIdIndexes(transaction: IDBTransaction): void {
  for (const [storeName, indexes] of Object.entries(SCHEMA_V2_ADDED_INDEXES)) {
    const store = transaction.objectStore(storeName)
    for (const index of indexes) {
      if (store.indexNames.contains(index.name)) continue
      store.createIndex(index.name, index.keyPath as string)
    }
  }
}

/**
 * Read the three owned stores, plan the backfill, and write it.
 *
 * The reads are issued together and the plan is made once the last one lands,
 * so the whole thing is one pass rather than a chain of dependent round trips.
 * `planProjectBackfill` is shared with the backup payload migration — see the
 * note there for why the decision lives outside this file.
 */
function backfill(transaction: IDBTransaction): void {
  const phases = transaction.objectStore(STORE.phases).getAll()
  const goals = transaction.objectStore(STORE.goals).getAll()
  const reviews = transaction.objectStore(STORE.weeklyReviews).getAll()

  let outstanding = 3
  const arrived = (): void => {
    outstanding -= 1
    if (outstanding > 0) return

    const plan = planProjectBackfill({
      phases: phases.result as PhaseRecord[],
      goals: goals.result as GoalRecord[],
      weeklyReviews: reviews.result as WeeklyReviewRecord[],
    })

    // A fresh install has nothing to carry. Setup creates the first project.
    if (plan !== undefined) writePlan(transaction, plan)
  }

  phases.onsuccess = arrived
  goals.onsuccess = arrived
  reviews.onsuccess = arrived
}

function writePlan(transaction: IDBTransaction, plan: BackfillPlan): void {
  transaction.objectStore(STORE.projects).put(plan.project)

  /* `put` rather than a cursor update: the records were read whole and are
     written back whole, and every one of them is keyed by `id`. A cursor would
     add a second traversal for no benefit. */
  for (const phase of plan.phases) transaction.objectStore(STORE.phases).put(phase)
  for (const goal of plan.goals) transaction.objectStore(STORE.goals).put(goal)
  for (const review of plan.weeklyReviews) {
    transaction.objectStore(STORE.weeklyReviews).put(review)
  }
}
